import React, {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import {
  CoreRow,
  RowSelectionState as TanstackRowSelectionState,
  Table,
  Updater as TanstackUpdater,
} from "@tanstack/react-table";
import { ROW_SELECTION_MODE } from "common/constants";
import { ActionKey } from "common/utils/actionBar";
import { SelectorParameters } from "common/utils/bulkOperations";
import { QueryDescription } from "common/utils/queryBuilder";
import { CheckboxProps } from "../../Inputs/Checkbox";

export interface TableRowsSelectionState extends Omit<
  SelectorParameters,
  "accountId"
> {
  totalSelected: number;
  showSelectAllButton: boolean;
  loadingAction: ActionKey | null;
}

type SetSelectionStateAction = {
  type: "setSelectionState";
  selectedIds: string[];
  unselectedIds: string[];
};

type SetAllModeAction = {
  type: "setAllMode";
};

type SetNoneModeAction = {
  type: "setNoneMode";
};

type SetQueryDescriptionAction = {
  type: "setQueryDescription";
  queryDescription: QueryDescription;
};

type SetTotalCountAction = {
  type: "setTotalCount";
  totalCount: number;
};

type SetShowSelectAllButtonAction = {
  type: "setShowSelectAllButton";
  show: boolean;
};

type SetLoadingAction = {
  type: "setLoadingAction";
  loadingAction: ActionKey | null;
};

type ResetAndSetQueryDescriptionAction = {
  type: "resetAndSetQueryDescription";
  queryDescription?: QueryDescription;
};

type TableRowsSelectionAction =
  | SetSelectionStateAction
  | SetAllModeAction
  | SetNoneModeAction
  | SetQueryDescriptionAction
  | SetTotalCountAction
  | SetShowSelectAllButtonAction
  | SetLoadingAction
  | ResetAndSetQueryDescriptionAction;

type TanstackRowSelectionHandler = (
  updater: TanstackUpdater<TanstackRowSelectionState>,
) => void;

export type TableRowsSelectionContextInstance = {
  rowSelection: TanstackRowSelectionState;
  onRowSelectionChange: TanstackRowSelectionHandler;
  state: TableRowsSelectionState;
  dispatch: Dispatch<TableRowsSelectionAction>;
  setTotalCount: (totalCount: number) => void;
  setLoadingAction: (loadingAction: ActionKey | null) => void;
  onSelectAllRows: () => void;
  selectionUpdate: (
    selectionIds: string[],
    shouldSelect: boolean,
  ) => TanstackRowSelectionState;
  resetAndSetQueryDescription: (queryDescription?: QueryDescription) => void;
  resetRowSelection: () => void;
  getHeaderCheckboxState: (
    currentPageRowIds: string[],
  ) => CheckboxProps["value"];
  getRowCheckboxState: (row: CoreRow<any>["original"]) => boolean;
  handleHeaderCheckboxChange: (
    table: Table<any>,
    currentPageRowIds: string[],
    checkboxValue: CheckboxProps["value"],
  ) => void;
  handleRowCheckboxChange: (
    row: CoreRow<any>["original"],
    checked: boolean,
  ) => void;
};

const initialTableRowsSelectionState: TableRowsSelectionState = {
  mode: ROW_SELECTION_MODE.NONE,
  ids: [],
  queryDescription: undefined,
  totalCount: 0,
  totalSelected: 0,
  showSelectAllButton: false,
  loadingAction: null,
};

export function tableRowsSelectionReducer(
  state: TableRowsSelectionState,
  action: TableRowsSelectionAction,
): TableRowsSelectionState {
  const setAllState = () => ({
    ...state,
    mode: ROW_SELECTION_MODE.ALL,
    ids: [],
    totalSelected: state.totalCount,
    showSelectAllButton: false,
  });

  const setAllExceptIdsState = (excludedIds: string[]) => ({
    ...state,
    mode: ROW_SELECTION_MODE.ALL_EXCEPT_IDS,
    ids: excludedIds,
    totalSelected: state.totalCount - excludedIds.length,
  });

  const setIdsState = (ids: string[]) => ({
    ...state,
    mode: ROW_SELECTION_MODE.IDS,
    ids: ids,
    totalSelected: ids.length,
  });

  const setNoneState = () => ({
    ...state,
    mode: ROW_SELECTION_MODE.NONE,
    ids: [],
    totalSelected: 0,
    showSelectAllButton: false,
    loadingAction: null,
  });

  function handleAllModeSelection(
    state: TableRowsSelectionState,
    unselectedIds: string[],
  ): TableRowsSelectionState {
    if (unselectedIds.length > 0) {
      // Transition to NONE if all rows are unselected
      if (unselectedIds.length >= state.totalCount) {
        return setNoneState();
      } else {
        // Transition to ALL_EXCEPT_IDS if some rows are unselected
        return setAllExceptIdsState(unselectedIds);
      }
    }
    return state;
  }

  function handleAllExceptIdsModeSelection(
    state: TableRowsSelectionState,
    selectedIds: string[],
    unselectedIds: string[],
  ): TableRowsSelectionState {
    // Row is unselected
    if (unselectedIds.length > 0) {
      const allUnselectedIds = [...new Set([...state.ids, ...unselectedIds])];
      // Transition to NONE mode if all rows are excluded
      if (allUnselectedIds.length >= state.totalCount) {
        return setNoneState();
      } else {
        return setAllExceptIdsState(allUnselectedIds);
      }
      // Row is selected
    } else if (selectedIds.length > 0) {
      const remainingUnselectedIds = state.ids.filter(
        id => !selectedIds.includes(id),
      );
      // Transition to ALL mode if all rows are selected
      if (remainingUnselectedIds.length === 0) {
        return setAllState();
      } else {
        return setAllExceptIdsState(remainingUnselectedIds);
      }
    }
    return state;
  }

  function handleIdsModeSelection(
    selectedIds: string[],
  ): TableRowsSelectionState {
    //  Transition to NONE mode if no rows are selected
    if (selectedIds.length === 0) {
      return setNoneState();
    } else {
      return setIdsState(selectedIds);
    }
  }

  switch (action.type) {
    case "setSelectionState":
      const { selectedIds, unselectedIds } = action;

      switch (state.mode) {
        case ROW_SELECTION_MODE.ALL:
          return handleAllModeSelection(state, unselectedIds);
        case ROW_SELECTION_MODE.ALL_EXCEPT_IDS:
          return handleAllExceptIdsModeSelection(
            state,
            selectedIds,
            unselectedIds,
          );
        case ROW_SELECTION_MODE.IDS:
          return handleIdsModeSelection(selectedIds);
        case ROW_SELECTION_MODE.NONE:
          // Transition to IDS mode if some rows are selected
          return setIdsState(selectedIds);
        default:
          break;
      }
      return state;
    case "setAllMode":
      return setAllState();
    case "setNoneMode":
      return setNoneState();
    case "setQueryDescription":
      return { ...state, queryDescription: action.queryDescription };
    case "setTotalCount":
      return { ...state, totalCount: action.totalCount };
    case "setShowSelectAllButton":
      return { ...state, showSelectAllButton: action.show };
    case "setLoadingAction":
      return { ...state, loadingAction: action.loadingAction };
    case "resetAndSetQueryDescription":
      return {
        ...initialTableRowsSelectionState,
        queryDescription: action.queryDescription,
      };
  }
}

const TableRowsSelectionContext =
  createContext<TableRowsSelectionContextInstance>({
    state: initialTableRowsSelectionState,
    dispatch: () => {},
    rowSelection: {},
    onRowSelectionChange: () => {},
    setTotalCount: () => {},
    setLoadingAction: () => {},
    onSelectAllRows: () => {},
    selectionUpdate: () => ({}),
    resetAndSetQueryDescription: (_queryDescription?: QueryDescription) => {},
    resetRowSelection: () => {},
    getHeaderCheckboxState: () => false,
    getRowCheckboxState: () => false,
    handleHeaderCheckboxChange: () => {},
    handleRowCheckboxChange: () => {},
  });

function selectionUpdate(
  selectionIds: string[],
  shouldSelect: boolean,
): TanstackRowSelectionState {
  return selectionIds.reduce((acc, id) => {
    acc[id] = shouldSelect;
    return acc;
  }, {} as TanstackRowSelectionState);
}

export function TableRowsSelectionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(
    tableRowsSelectionReducer,
    initialTableRowsSelectionState,
  );

  const rowSelection: TanstackRowSelectionState = useMemo(() => {
    switch (state.mode) {
      case ROW_SELECTION_MODE.ALL:
        return {};
      case ROW_SELECTION_MODE.ALL_EXCEPT_IDS:
        return selectionUpdate(state.ids, false);
      default:
        return selectionUpdate(state.ids, true);
    }
  }, [state.mode, state.ids]);

  const onRowSelectionChange: TanstackRowSelectionHandler = useCallback(
    updater => {
      let result: TanstackRowSelectionState;

      if (typeof updater === "function") {
        result = updater(rowSelection);
      } else {
        result = updater;
      }

      const selectedIds = Object.keys(result).filter(id => result[id]);
      const unselectedIds = Object.keys(result).filter(id => !result[id]);

      dispatch({
        type: "setSelectionState",
        selectedIds: selectedIds,
        unselectedIds: unselectedIds,
      });
    },
    [rowSelection, dispatch],
  );

  const setTotalCount = useCallback(
    (totalCount: number) => {
      dispatch({ type: "setTotalCount", totalCount });
    },
    [dispatch],
  );

  const setLoadingAction = useCallback(
    (loadingAction: ActionKey | null) => {
      dispatch({ type: "setLoadingAction", loadingAction });
    },
    [dispatch],
  );

  const resetAndSetQueryDescription = useCallback(
    (queryDescription?: QueryDescription) => {
      dispatch({ type: "resetAndSetQueryDescription", queryDescription });
    },
    [dispatch],
  );

  const resetRowSelection = useCallback(() => {
    dispatch({ type: "setNoneMode" });
  }, [dispatch]);

  const onSelectAllRows = useCallback(() => {
    dispatch({ type: "setAllMode" });
  }, [dispatch]);

  const getHeaderCheckboxState = (
    currentPageRowIds: string[],
  ): CheckboxProps["value"] => {
    let isChecked = false;
    let isIndeterminate = false;

    switch (state.mode) {
      case ROW_SELECTION_MODE.ALL:
        isChecked = true;
        break;
      case ROW_SELECTION_MODE.ALL_EXCEPT_IDS:
        const excludedOnCurrentPage = currentPageRowIds.filter((id: string) =>
          state.ids.includes(id),
        );
        const selectedOnCurrentPage = currentPageRowIds.filter(
          (id: string) => !state.ids.includes(id),
        );

        if (excludedOnCurrentPage.length === 0) {
          isChecked = true;
        } else if (selectedOnCurrentPage.length === 0) {
          isChecked = false;
        } else {
          isIndeterminate = true;
        }
        break;
      default:
        const someRowsSelected = currentPageRowIds.some((id: string) =>
          state.ids.includes(id),
        );
        const allRowsSelected =
          currentPageRowIds.length > 0 &&
          currentPageRowIds.every((id: string) => state.ids.includes(id));
        isChecked = allRowsSelected;
        isIndeterminate = someRowsSelected && !allRowsSelected;
        break;
    }

    const checkboxValue =
      isChecked ? true
      : isIndeterminate ? "indeterminate"
      : false;

    return checkboxValue;
  };

  const getRowCheckboxState = (row: CoreRow<any>["original"]) => {
    const rowId = row.id;
    return (
      state.mode === ROW_SELECTION_MODE.ALL ||
      (state.mode === ROW_SELECTION_MODE.ALL_EXCEPT_IDS &&
        !state.ids.includes(rowId)) ||
      row.getIsSelected()
    );
  };

  const handleHeaderCheckboxChange = (
    table: Table<any>,
    currentPageRowIds: string[],
    checkboxValue: CheckboxProps["value"],
  ) => {
    if (state.loadingAction !== null) return;

    const shouldUnToggle =
      checkboxValue === true || checkboxValue === "indeterminate";
    let selectedIds;

    switch (state.mode) {
      case ROW_SELECTION_MODE.ALL:
        selectedIds = selectionUpdate(currentPageRowIds, false);
        onRowSelectionChange(selectedIds);
        break;
      case ROW_SELECTION_MODE.ALL_EXCEPT_IDS:
        const shouldSelect = currentPageRowIds.every((id: string) =>
          state.ids.includes(id),
        );
        selectedIds = selectionUpdate(currentPageRowIds, shouldSelect);
        onRowSelectionChange(selectedIds);
        break;
      default:
        if (shouldUnToggle) {
          table.toggleAllRowsSelected(false);
          dispatch({ type: "setShowSelectAllButton", show: false });
        } else {
          table.toggleAllRowsSelected(true);
          if (state.totalCount > currentPageRowIds.length) {
            dispatch({ type: "setShowSelectAllButton", show: true });
          }
        }
    }
  };

  const handleRowCheckboxChange = (
    row: CoreRow<any>["original"],
    checked: boolean,
  ) => {
    if (state.loadingAction !== null) return;

    const rowId = row.id;
    if (state.mode === ROW_SELECTION_MODE.ALL) {
      onRowSelectionChange({ [rowId]: false });
    } else if (state.mode === ROW_SELECTION_MODE.ALL_EXCEPT_IDS) {
      state.ids.includes(rowId) ?
        onRowSelectionChange({ [rowId]: true })
      : onRowSelectionChange({ [rowId]: false });
    } else {
      const handler = row.getToggleSelectedHandler();
      handler({ target: { checked } });
    }
  };

  return (
    <TableRowsSelectionContext.Provider
      value={{
        state,
        dispatch,
        rowSelection,
        onRowSelectionChange,
        setTotalCount,
        setLoadingAction,
        onSelectAllRows,
        selectionUpdate,
        resetAndSetQueryDescription,
        resetRowSelection,
        getHeaderCheckboxState,
        getRowCheckboxState,
        handleHeaderCheckboxChange,
        handleRowCheckboxChange,
      }}
    >
      {children}
    </TableRowsSelectionContext.Provider>
  );
}

export function useTableRowsSelectionState() {
  const context = useContext(TableRowsSelectionContext);

  if (!context) {
    throw new Error(
      "useTableRowsSelectionState must be used within a TableRowsSelectionProvider",
    );
  }

  return context;
}
